import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, Upload } from 'lucide-react';
import { 
    getBasicInfoAdmin, 
    updateBasicInfo,
    uploadImageToCloudinary
} from '../../Firebase/Firebase';

const BasicInfoSection = () => {
  const [basicInfo, setBasicInfo] = useState({
    address: '',
    email: '',
    contactNo: '',
    chefImageUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    address: '',
    email: '',
    contactNo: '',
    chefImage: null
  });

  // Load basic info on component mount
  useEffect(() => {
    loadBasicInfo();
  }, []);

  // Keep form data in sync with basicInfo
  useEffect(() => {
    if (basicInfo.address || basicInfo.email || basicInfo.contactNo) {
      setFormData({
        address: basicInfo.address || '',
        email: basicInfo.email || '',
        contactNo: basicInfo.contactNo || '',
        chefImage: null
      });
      setImagePreview(basicInfo.chefImageUrl || '');
    }
  }, [basicInfo]);

  const loadBasicInfo = () => {
    setLoading(true);
    try {
      getBasicInfoAdmin((info) => {
        if (info && info.length > 0) {
          const loadedInfo = info[0];
          setBasicInfo(loadedInfo);
          // Initialize form data with loaded values
          setFormData({
            address: loadedInfo.address || '',
            email: loadedInfo.email || '',
            contactNo: loadedInfo.contactNo || '',
            chefImage: null
          });
          setImagePreview(loadedInfo.chefImageUrl || '');
        } else {
          // If no data exists, initialize with empty values
          setFormData({
            address: '',
            email: '',
            contactNo: '',
            chefImage: null
          });
          setImagePreview('');
        }
      });
    } catch (error) {
      console.error('Error loading basic info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, chefImage: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview('');
      setFormData(prev => ({ ...prev, chefImage: null }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    setLoading(true);
    try {
      let chefImageUrl = basicInfo.chefImageUrl;
      
      if (formData.chefImage) {
        try {
          chefImageUrl = await uploadImageToCloudinary(formData.chefImage);
        } catch (uploadError) {
          console.error('Chef image upload failed:', uploadError);
          chefImageUrl = basicInfo.chefImageUrl;
        }
      }

      const infoData = {
        address: formData.address.trim(),
        email: formData.email.trim(),
        contactNo: formData.contactNo.trim(),
        chefImageUrl
      };

      await updateBasicInfo(infoData);
      
      setBasicInfo(prev => ({ ...prev, ...infoData }));
      setIsEditing(false);
      setImagePreview('');
    } catch (error) {
      console.error('Error saving basic info:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    // Restore form data to match current basic info
    setFormData({
      address: basicInfo.address || '',
      email: basicInfo.email || '',
      contactNo: basicInfo.contactNo || '',
      chefImage: null
    });
    setImagePreview(basicInfo.chefImageUrl || '');
  };

  return (
    <>
      {/* Basic Info Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Basic Information</h2>
          {!isEditing && (
            <button
              onClick={startEditing}
              className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Edit</span>
              <span className="sm:hidden">Edit</span>
            </button>
          )}
        </div>
        
        {/* Basic Info Display/Edit */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {loading && !basicInfo.address && !basicInfo.email && !basicInfo.contactNo ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Information */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    {isEditing ? (
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 text-gray-800"
                        placeholder="Enter address"
                      />
                    ) : (
                      <p className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                        {basicInfo.address || 'No address set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 text-gray-800"
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                        {basicInfo.email || 'No email set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="contactNo"
                        value={formData.contactNo}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 text-gray-800"
                        placeholder="Enter contact number"
                      />
                    ) : (
                      <p className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                        {basicInfo.contactNo || 'No contact number set'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Chef Image */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Chef Image</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Chef Photo</label>
                    {isEditing ? (
                      <div className="space-y-4">
                        <label className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-orange-400 transition-colors text-gray-700">
                          <Upload className="w-5 h-5 mr-2" />
                          <span>Choose Chef Image</span>
                          <input
                            type="file"
                            onChange={handleImageChange}
                            accept="image/*"
                            className="hidden"
                          />
                        </label>
                        {imagePreview && (
                          <div className="relative inline-block">
                            <img
                              src={imagePreview}
                              alt="Chef preview"
                              className="w-32 h-32 object-cover rounded-xl shadow-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const event = { target: { files: null } };
                                handleImageChange(event);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center space-x-4">
                        <img
                          src={basicInfo.chefImageUrl || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'}
                          alt="Chef"
                          className="w-32 h-32 object-cover rounded-xl shadow-lg"
                        />
                        <p className="text-gray-600">
                          {basicInfo.chefImageUrl ? 'Chef image uploaded' : 'No chef image set'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200 justify-end">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={cancelEditing}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BasicInfoSection; 